import React, { useMemo, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, Minus, Plus } from "lucide-react";
import type {
  HotTopicCard,
  NewsCategoryOption,
  PreviewNewsCard,
  SelectedCategory,
} from "../../features/kquant/types";
import { Button, Panel } from "../../components/ui/primitives";
import { HotTopicCardView } from "../../features/kquant/components/HotTopicCardView";
import { PreviewCardView } from "../../features/kquant/components/PreviewCardView";

export function HotTopicsSection({
  categories,
  selections,
  setSelections,
  hotTopics,
  preview,
  onPreview,
}: {
  categories: NewsCategoryOption[];
  selections: SelectedCategory[];
  setSelections: React.Dispatch<React.SetStateAction<SelectedCategory[]>>;
  hotTopics: HotTopicCard[] | null;
  preview: PreviewNewsCard | null;
  onPreview: () => void;
}) {
  const [isCatOpen, setIsCatOpen] = React.useState(false);
  const catAnchorRef = useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (!isCatOpen) return;
      const target = e.target as Node;
      const anchor = catAnchorRef.current;
      if (!anchor) return;
      if (anchor.contains(target)) return;
      setIsCatOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [isCatOpen]);

  const selectionChips = useMemo(() => {
    const map = new Map(categories.map((c) => [c.key, c.label]));
    return selections.map((s) => ({ ...s, label: map.get(s.key) ?? s.key }));
  }, [categories, selections]);

  function updateSelection(i: number, patch: Partial<SelectedCategory>) {
    setSelections((prev) => {
      const next = [...prev];
      next[i] = { ...next[i], ...patch };
      next[i].count = Math.max(1, Math.min(9, next[i].count));
      return next;
    });
  }

  function removeSelection(i: number) {
    setSelections((prev) => prev.filter((_, idx) => idx !== i));
  }

  function addSelection() {
    setSelections((prev) => {
      const used = new Set(prev.map((p) => p.key));
      const candidate =
        categories.find((c) => !used.has(c.key))?.key ??
        categories[0]?.key ??
        "finance";
      return [...prev, { key: candidate, count: 3 }];
    });
  }

  const hotLeft = hotTopics?.[0];
  const hotMid = hotTopics?.[1];

  return (
    <section>
      <Panel className="p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
          <div className="flex items-baseline gap-4">
            <div className="text-sm font-semibold">Today’s Hot Topics</div>
            <div className="text-xs text-white/55">
              Automated Daily Selection of Key News
            </div>
          </div>

          <div className="relative w-full sm:w-auto" ref={catAnchorRef}>
            <button
              onClick={() => setIsCatOpen((v) => !v)}
              className="flex w-full items-center justify-between gap-3 rounded-full border border-white/15 bg-white/5 px-4 py-2 text-left text-xs text-white/70 shadow-[0_14px_40px_-26px_rgba(0,0,0,0.75)] hover:bg-white/7 sm:w-auto"
            >
              <span className="flex min-w-0 flex-1 flex-col gap-1 sm:flex-row sm:items-center sm:gap-3">
                <span className="shrink-0 text-white/65">
                  <span className="hidden sm:inline">Select News Category:</span>
                  <span className="sm:hidden">Category:</span>
                </span>
                <span className="flex min-w-0 flex-1 flex-wrap items-center gap-2 sm:flex-nowrap">
                  {selectionChips.map((s) => (
                    <span
                      key={s.key}
                      className="inline-flex shrink-0 items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1"
                    >
                      <span className="capitalize">{s.label}</span>
                      <span className="grid h-4 w-4 place-items-center rounded-full bg-white/10 text-[10px] text-white/70">
                        {s.count}
                      </span>
                    </span>
                  ))}
                </span>
              </span>
              <ChevronDown className="h-4 w-4 shrink-0 text-white/55" />
            </button>

            <AnimatePresence>
              {isCatOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 8, scale: 0.98 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 8, scale: 0.98 }}
                  transition={{ duration: 0.18 }}
                  className="absolute left-0 right-0 top-[46px] z-20 w-full rounded-2xl border border-white/15 bg-[#0a0c24]/90 p-4 shadow-[0_28px_70px_-40px_rgba(0,0,0,0.95)] backdrop-blur-xl sm:left-auto sm:right-0 sm:w-[420px]"
                >
                  <div className="mb-3 text-xs font-semibold text-white/75">
                    Select News Category:
                  </div>

                  <div className="space-y-2">
                    {selections.map((s, i) => (
                      <div key={`${s.key}-${i}`} className="flex items-center gap-2">
                        <select
                          value={s.key}
                          onChange={(e) => updateSelection(i, { key: e.target.value })}
                          style={{ colorScheme: "dark" }}
                          className="kq-select h-9 w-[180px] rounded-xl border border-white/10 bg-white/5 px-3 text-xs text-white/80 outline-none"
                        >
                          {categories.map((c) => (
                            <option
                              key={c.key}
                              value={c.key}
                              style={{
                                backgroundColor: "rgba(10, 12, 36, 0.98)",
                                color: "rgba(255, 255, 255, 0.82)",
                              }}
                            >
                              {c.label}
                            </option>
                          ))}
                        </select>

                        <div className="flex h-9 w-[170px] items-center justify-between rounded-xl border border-white/10 bg-white/5 px-3">
                          <span className="text-xs text-white/50">{s.count}</span>
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => updateSelection(i, { count: s.count - 1 })}
                              className="grid h-7 w-7 place-items-center rounded-full border border-white/10 bg-white/5 hover:bg-white/10"
                              aria-label="decrease"
                            >
                              <Minus className="h-3.5 w-3.5 text-white/70" />
                            </button>
                            <button
                              onClick={() => updateSelection(i, { count: s.count + 1 })}
                              className="grid h-7 w-7 place-items-center rounded-full border border-white/10 bg-white/5 hover:bg-white/10"
                              aria-label="increase"
                            >
                              <Plus className="h-3.5 w-3.5 text-white/70" />
                            </button>
                          </div>
                        </div>

                        <button
                          onClick={() => removeSelection(i)}
                          className="grid h-9 w-9 place-items-center rounded-full border border-white/10 bg-white/5 hover:bg-white/10"
                          aria-label="remove"
                        >
                          <Minus className="h-4 w-4 text-white/70" />
                        </button>
                      </div>
                    ))}
                  </div>

                  <div className="mt-3 flex items-center justify-between">
                    <Button variant="outline" size="sm" onClick={addSelection} className="rounded-xl">
                      <Plus className="h-4 w-4" />
                      Add news categories
                    </Button>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => {
                        onPreview();
                        setIsCatOpen(false);
                      }}
                      className="rounded-xl px-6"
                    >
                      Preview
                    </Button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-1 gap-5 md:grid-cols-3">
          <HotTopicCardView card={hotLeft} />
          <HotTopicCardView card={hotMid} />
          <PreviewCardView card={preview} loading={!preview} />
        </div>
      </Panel>
    </section>
  );
}
